import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToasts } from 'react-toast-notifications';
import ReactTooltip from 'react-tooltip';
import dayjs from 'dayjs';
import { saveAs } from 'file-saver';
import { FiDownload } from 'react-icons/fi';
import Box from 'common/components/box/Box';
import LoadingSpinner from 'common/components/loading/LoadingSpinner';
import Loading from 'common/services/Loading';
import Utils from 'common/services/Utils';
import DashboardService from 'api/dashboard/DashboardService';
import { PlafondConsumptionItemDto } from 'api/dashboard/dtos/PlafondConsumptionDashboardDto';
import { FiltersFormModel } from '../filters/FiltersView';
import { SelectValueLabel } from 'common/types/SelectValueLabel';
import { useDashboardVehicleScope, vehicleScopeCriteria } from '../hooks/useDashboardVehicleScope';
import useWidgetFilters from '../hooks/useWidgetFilters';
import WidgetMonthPicker from '../components/WidgetMonthPicker';
import WidgetNoData from '../components/WidgetNoData';
import useInfiniteReveal from '../components/useInfiniteReveal';
import tableStyles from '../dashboardTable/DashboardTable.module.scss';
import infiniteStyles from '../components/useInfiniteReveal.module.scss';
import styles from './PlafondConsumption.module.scss';

const PAGE_SIZE = 10;

/**
 * Dentro do limite vs ultrapassado é uma distinção semântica (medidor de progresso), não um ranking
 * — daí não usar a escala teal dos gráficos de barras horizontais de topo.
 */
const WITHIN_COLOR = '#16CCC3';
const EXCEEDED_COLOR = '#DC4C4C';

type Props = {
	filters?: FiltersFormModel;
	vehicles?: SelectValueLabel[];
};

/**
 * Task #66600 — consumo do mês face aos plafonds definidos.
 *
 * O mesmo widget serve os dois contextos: no detalhe do ativo o scoping por vehicleId limita-o
 * àquele ativo; no painel geral mostra a frota, com os plafonds ultrapassados primeiro.
 */
const PlafondConsumption = (props: Props) => {
	const { t } = useTranslation();
	const { addToast } = useToasts();
	const [data, setData] = useState<PlafondConsumptionItemDto[] | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const { dateFrom, dateTo, setDateRange } = useWidgetFilters('plafondConsumption');
	const scopeVehicleId = useDashboardVehicleScope();
	const vehicleScope = vehicleScopeCriteria(scopeVehicleId);

	useEffect(() => {
		let cancelled = false;
		const fetchData = async () => {
			setIsLoading(true);
			try {
				const criteria = props.filters
					? { ...props.filters }
					: { dateFrom, dateTo, ...vehicleScope };
				const result = await DashboardService.getPlafondConsumption(criteria);
				if (!cancelled) setData(result?.items || []);
			} catch {
				if (!cancelled) addToast(t('common.messages.error_load_info'), { appearance: 'error' });
			} finally {
				if (!cancelled) setIsLoading(false);
			}
		};
		fetchData();
		return () => { cancelled = true; };
	}, [props.filters, dateFrom, dateTo, scopeVehicleId]);

	/**
	 * A agregação devolve uma linha por (plafond, mês). O widget mostra o mês de referência — o
	 * último do período — porque é esse que o utilizador está a acompanhar.
	 */
	const rows = useMemo(() => {
		if (!data || data.length === 0) { return []; }

		const latestMonth = data
			.map(x => new Date(x.month).getTime())
			.reduce((max, current) => (current > max ? current : max), 0);

		return data
			.filter(x => new Date(x.month).getTime() === latestMonth)
			.sort((a, b) => b.usagePercentage - a.usagePercentage);
	}, [data]);

	const { visibleItems, sentinelRef, hasMore } = useInfiniteReveal(rows, PAGE_SIZE);

	const exceededCount = rows.filter(x => x.exceeded).length;

	/** Exporta exactamente o que o widget está a mostrar (mesmo âmbito e mesmo período). */
	const onExport = async () => {
		try {
			Loading.show();
			const criteria = props.filters ? { ...props.filters } : { dateFrom, dateTo, ...vehicleScope };
			const report = await DashboardService.getPlafondConsumptionReport(criteria);
			const title = t('vehicle.plafonds.report_title' as any) as string;
			saveAs(report, `${title}_${dayjs().format('YYYY_MM_DD_HH_mm_ss')}.xlsx`);
		} catch {
			addToast(t('common.messages.error_load_info'), { appearance: 'error' });
		} finally {
			Loading.hide();
		}
	};

	const buildTooltip = (item: PlafondConsumptionItemDto) => {
		const lines = [
			`${t('vehicle.plafonds.consumed' as any)}: ${Utils.formatCurrency(item.consumed)}`,
			`${t('vehicle.plafonds.limit' as any)}: ${Utils.formatCurrency(item.monthlyLimit)}`,
		];
		if (item.exceeded) {
			lines.push(`${t('vehicle.plafonds.overage' as any)}: ${Utils.formatCurrency(item.overage)}`);
		}
		return lines.join('<br />');
	};

	return (
		<Box
			className={tableStyles.container}
			title={t('vehicle.plafonds.widget_title' as any) as string}
			subTitleTooltip={t('vehicle.plafonds.widget_tooltip' as any) as string}
			renderOptions={<div className={styles.options}>
				<FiDownload
					className={styles.exportIcon}
					title={t('vehicle.plafonds.report_title' as any) as string}
					onClick={onExport}
				/>
				<WidgetMonthPicker dateFrom={dateFrom} dateTo={dateTo} onChange={setDateRange} />
			</div>}
		>
			{isLoading ? (
				<div className={tableStyles.centerAlign}>
					<LoadingSpinner />
				</div>
			) : rows.length === 0 ? (
				<WidgetNoData message={t('vehicle.plafonds.widget_no_plafonds' as any) as string} />
			) : (
				<>
					<div className={styles.summary}>
						<span className={exceededCount > 0 ? styles.summaryExceeded : styles.summaryOk}>
							{exceededCount > 0
								? `${exceededCount} ${t('vehicle.plafonds.exceeded' as any)}`
								: (t('vehicle.plafonds.within' as any) as string)}
						</span>
						<span className={styles.summaryMonth}>{dayjs(rows[0].month).format('MMMM YYYY')}</span>
					</div>

					<div className={styles.progressList}>
						{visibleItems.map(item => {
							const percent = Math.round(item.usagePercentage);
							const color = item.exceeded ? EXCEEDED_COLOR : WITHIN_COLOR;
							return (
								<div
									key={item.plafondId}
									className={styles.progressRow}
									// Click-through sempre em nova aba, como os restantes widgets.
									onClick={() => window.open(`/vehicles/details/${item.vehicleId}`, '_blank')}
								>
									<div className={styles.progressLabel}>
										{/* No contexto do ativo a matrícula é redundante. */}
										{!scopeVehicleId && <span className={styles.registration}>{item.registrationNumber}</span>}
										<span className={styles.designation}>{item.designation}</span>
									</div>
									<div className={styles.progressTrack}>
										<div
											className={styles.progressFill}
											style={{ width: `${Math.min(percent, 100)}%`, background: color }}
										/>
									</div>
									<div className={styles.progressPercent} style={{ color }}>
										{percent}%
									</div>
									<div
										className={styles.progressValue}
										data-tip={buildTooltip(item)}
										data-for="plafond-consumption-tooltip"
									>
										{Utils.formatCurrency(item.consumed)} / {Utils.formatCurrency(item.monthlyLimit)}
									</div>
								</div>
							);
						})}
						{hasMore && <div ref={sentinelRef} className={infiniteStyles.sentinel} />}
					</div>
					<ReactTooltip id="plafond-consumption-tooltip" className={styles.tooltip} effect="solid" multiline={true} />
				</>
			)}
		</Box>
	);
};

export default PlafondConsumption;
